import { useState } from "react";

import { fecha as fechaCorta } from "../../shared/rto";
import {
  MOTIVO_SUGERIDO,
  discrepa,
  firma,
  hayDeclarada,
  laManda,
  sePuedeDeclarar,
  sugerida,
} from "../../shared/rtoDeclarada";

/* Escribir la fecha de RTO que viene en el título.
 *
 * ── POR QUÉ ES UN COMPONENTE Y NO UN CAMPO ADENTRO DE CADA PANTALLA ──────
 *
 * Se usa en las tres: la ficha de la unidad, el RTO de tractores y el de
 * furgones. Copiado, el día que cambie qué se dice cuando el CENT aparece con
 * otra fecha, una de las tres se quedaría contestando lo viejo — y nadie mira
 * las tres el mismo día, así que la diferencia vive meses. Es la razón por la
 * que `VeredictoRTO`, `Ubicacion` y `ChipUsuario` salieron de adentro de sus
 * pantallas.
 *
 * ── LO QUE NO PUEDE DECIR MAL ────────────────────────────────────────────
 *
 *   · **El CENT gana y se dice.** Con una declarada distinta, salen LAS DOS,
 *     y la del título va marcada como que no se está usando. Elegir en
 *     silencio dejaría al que la cargó creyendo que sirvió.
 *   · **«No pude guardar» no se dice como «guardado».** El error queda a la
 *     vista con su motivo; un cartel verde sobre una escritura que falló es
 *     exactamente el problema que esto vino a arreglar en otras pantallas.
 *   · **No pregunta al borrar.** Escribe una columna y el campo de al lado la
 *     vuelve a poner: confirmar lo reversible enseña a apretar «sí» sin leer.
 *     Misma regla que el toggle de baja del chofer.
 */
const DeclararRTO = ({ estado, dominio, puedeCargar, onGuardar, onBorrar }) => {
  const [abierto, setAbierto] = useState(false);
  const [vence, setVence] = useState("");
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState(null);
  const [yendo, setYendo] = useState(false);

  if (!estado || !dominio) return null;

  const hay = hayDeclarada(estado);
  const tapada = hay && laManda(estado);

  const abrir = () => {
    /* Se abre con lo que YA HAY, y sólo si no hay nada se ofrece el año desde
     * hoy. Abrir con la sugerencia encima de una fecha cargada la pisaría con
     * un valor calculado, que es justo lo que este campo existe para no
     * hacer. */
    setVence(estado.declarada || sugerida());
    setMotivo(estado.declarada_motivo || (hay ? "" : MOTIVO_SUGERIDO));
    setError(null);
    setAbierto(true);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setError(null);
    setYendo(true);
    try {
      await onGuardar(dominio, vence, motivo);
      setAbierto(false);
    } catch (err) {
      setError(err.message || "No se pudo guardar");
    } finally {
      setYendo(false);
    }
  };

  const borrar = async () => {
    setError(null);
    setYendo(true);
    try {
      await onBorrar(dominio);
      setAbierto(false);
    } catch (err) {
      setError(err.message || "No se pudo borrar");
    } finally {
      setYendo(false);
    }
  };

  return (
    <div className="rto-declarada">
      {/* LA DECLARADA SE VE AUNQUE PIERDA. Cuando el CENT tiene fecha propia y
          alguien había cargado otra, ver las dos es lo único que permite
          notarlo — y el que la tipeó, enterarse de que no se usa. */}
      {hay && (
        <div className={"msg-datos" + (tapada ? " tenue" : "")}>
          <div>
            <span className="et">
              {tapada ? "Declarada (no se usa)" : "Declarada a mano"}
            </span>
            <span title={firma(estado) || undefined}>
              {fechaCorta(estado.declarada)}
              {estado.declarada_motivo && (
                <em className="tk-item-meta"> · {estado.declarada_motivo}</em>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Apareció el CENT y dice otra cosa. NO se resuelve solo: la fecha del
          organismo ya está mandando, y esto avisa para que alguien vaya a
          borrar la vieja. Igual fecha no es discrepancia. */}
      {discrepa(estado) && (
        <p className="tk-item-meta rto-ojo">
          El CENT contestó {fechaCorta(estado.vence)} y acá había cargado{" "}
          {fechaCorta(estado.declarada)}. Manda la del CENT.
        </p>
      )}

      {puedeCargar && sePuedeDeclarar(estado) && !abierto && (
        <button type="button" className="rto-link" onClick={abrir}>
          {hay ? "Corregir la fecha del título" : "Cargar la fecha del título"}
        </button>
      )}

      {abierto && (
        <form className="rto-declarada__form" onSubmit={guardar}>
          <label>
            <span className="et">Vence</span>
            <input
              type="date"
              value={vence}
              required
              onChange={(ev) => setVence(ev.target.value)}
              aria-label="Fecha de vencimiento del título"
              autoFocus
            />
          </label>
          <label>
            <span className="et">Por qué</span>
            <input
              type="text"
              value={motivo}
              placeholder={MOTIVO_SUGERIDO}
              onChange={(ev) => setMotivo(ev.target.value)}
              aria-label="Motivo"
            />
          </label>
          <div className="rto-declarada__botones">
            <button className="tk-boton" disabled={yendo || !vence}>
              Guardar
            </button>
            <button
              type="button"
              className="rto-link"
              onClick={() => setAbierto(false)}
            >
              Cancelar
            </button>
            {hay && (
              <button
                type="button"
                className="rto-link rto-ojo"
                onClick={borrar}
                disabled={yendo}
              >
                Borrarla
              </button>
            )}
          </div>
          {/* El error se dice con su motivo y la fila NO se cierra: cerrada,
              «no tenés permiso» y «se guardó» se verían igual. */}
          {error && <p className="tk-item-meta rto-ojo">{error}</p>}
        </form>
      )}
    </div>
  );
};

export default DeclararRTO;
